import React from "react";
import styled, { css } from "styled-components";
import { Tick02Icon } from "@hugeicons/core-free-icons";
import HugeIcon from "src/components/HugeIcon";
import { colors } from "src/theme/colors";
import { line } from "src/theme/ui";

/**
 * Stepper — where you are in a short sequence.
 *
 * Ant Design's Steps was doing this job in the smartfolio builder, and it
 * arrived as a different product: a 32px accent disc carrying a 16px numeral,
 * a 16px title next to it, and a rule between steps that ran the full width
 * of a 900px dialog. In a dialog whose body is a table of thirteen-pixel
 * rows, the thing telling you which of two steps you are on was the largest
 * type on screen.
 *
 * So: a 22px disc and a 12.5px label — one line of chrome above the work,
 * which is all a two-step sequence needs. The state is carried three ways
 * over, so colour is never doing it alone: DONE takes the accent and a tick,
 * HERE takes the accent and its number and is the only step set in the ink
 * used for headings, and AHEAD is a grey ring with a grey numeral. The rail
 * between them fills up to where you are, which is the one thing a stepper
 * says that a title cannot.
 */

export interface StepperStep {
  /** The step's name. */
  title: string;
}

export interface StepperProps {
  steps: StepperStep[];
  /** Zero-based. */
  current: number;
  className?: string;
}

const Row = styled.ol`
  display: flex;
  align-items: center;
  gap: 10px;
  width: 100%;
  margin: 0;
  padding: 0;
  list-style: none;
`;

const Item = styled.li`
  display: flex;
  align-items: center;
  gap: 8px;
  flex: none;
`;

const Disc = styled.span<{ $state: "done" | "here" | "ahead" }>`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 22px;
  height: 22px;
  flex: none;
  border-radius: 50%;
  font-size: 11px;
  font-weight: 600;
  font-variant-numeric: tabular-nums;

  ${({ $state }) =>
    $state === "ahead"
      ? css`
          border: 1px solid ${line.field};
          background: ${colors.white};
          color: ${colors.gray[400]};
        `
      : css`
          background: ${colors.primary[500]};
          color: ${colors.white};
        `}
`;

const Label = styled.span<{ $state: "done" | "here" | "ahead" }>`
  font-size: 12.5px;
  white-space: nowrap;

  ${({ $state }) =>
    $state === "here"
      ? css`
          font-weight: 600;
          color: ${colors.gray[900]};
        `
      : $state === "done"
      ? css`
          font-weight: 500;
          color: ${colors.gray[600]};
        `
      : css`
          font-weight: 500;
          color: ${colors.gray[400]};
        `}
`;

/* The rail takes the room left over, so the steps stay their own size however
   wide the dialog is — and it is the only part of the control that stretches,
   which is what makes the fill readable as progress. */
const Rail = styled.span<{ $on: boolean }>`
  flex: 1;
  min-width: 16px;
  height: 1px;
  background: ${({ $on }) => ($on ? colors.primary[200] : line.hairline)};
`;

const Stepper: React.FC<StepperProps> = ({ steps, current, className }) => (
  <Row className={className}>
    {steps.map((step, index) => {
      const state =
        index < current ? "done" : index === current ? "here" : "ahead";
      return (
        <React.Fragment key={step.title}>
          <Item aria-current={state === "here" ? "step" : undefined}>
            <Disc $state={state}>
              {state === "done" ? (
                <HugeIcon icon={Tick02Icon} size={12} strokeWidth={2.6} />
              ) : (
                index + 1
              )}
            </Disc>
            <Label $state={state}>{step.title}</Label>
          </Item>
          {index < steps.length - 1 && <Rail $on={index < current} />}
        </React.Fragment>
      );
    })}
  </Row>
);

export default Stepper;
